import type {  ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Obj, Str } from '@livecontrol/core-utils';
import { User } from '@livecontrol/scheduler/model';
import { Errors } from '@livecontrol/scheduler/store';
import type { MutationResult } from '@livecontrol/scheduler/store';
import { useCallback, useState } from 'react';
import { Token } from '../../model';

interface TVariables {
  email: string;
  password: string;
}

interface TData {
  authenticate?: {
    token: Token.JWT;
    webplayer_token: string;
    user: {
      role: {
        id: unknown;
      };
      command_center_access: boolean;
      commandCenterAccess: {
        id: string;
        device_access: boolean;
        event_access: boolean;
        installer_access: boolean;
        installer_management: boolean;
      };
      status: {
        id: unknown;
      };
    };
  };
}

type Args = TVariables;

const MUTATION = gql`
  mutation Authenticate($email: String!, $password: String!) {
    authenticate(input: { email: $email, password: $password }) {
      token
      webplayer_token
      user {
        role {
          id
        }
        command_center_access
        commandCenterAccess {
          id
          device_access
          event_access
          installer_access
          installer_management
        }
        status {
          id
        }
      }
    }
  }
`;

export const useAuthenticate = (): [
  (args: Args) => Promise<Token | undefined>,
  MutationResult<{ token?: Token; webplayerToken?: string}, 'tokens'>
] => {

  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);
  const [error, setError] = useState<Error | undefined>();
  const [token, setToken] = useState<Token | undefined>();
  const [webplayerToken, setWebplayerToken] = useState<string | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Token | undefined> => {
        let token_: Token | undefined;
        let webplayerToken_: string | undefined;

        try {
          // Parse the input arguments
          const variables = {
            email: Str.normalize(args.email),
            password: Str.normalize(args.password)
          };

          // Validate the input

          if (!Obj.isHydrated(variables)) {
            throw Errors.badRequest();
          }

          // Execute the GraphQL mutation

          const response = await mutation({variables})
          .then(({ data }: FetchResult<TData>) => data?.authenticate)
          .catch((_error: ApolloError) => {
            throw Errors.serverError();
          });


          // Parse the server response
          const role = User.Role.normalize(response?.user.role.id);

          const status = User.Status.normalize(response?.user.status.id);

          const candidate = Token.decode(response?.token);

          webplayerToken_ = response?.webplayer_token;

          if (!role || !status || !candidate || !webplayerToken_) {
            throw Errors.serverError();
          }

          const { device_access, event_access, installer_access } = response?.user.commandCenterAccess ?? {};
          const hasAnyAccess = !!device_access || !!event_access || !!installer_access;

          if (!hasAnyAccess) {
            throw new Error('Only authorized users can login to this application.');
          } else if (status !== User.Status.Active) {
            throw new Error('Your account is not active.');
          }

          // This token is good
          token_ = candidate;
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setToken(token_);
        setWebplayerToken(webplayerToken_);

        return token_;
      },
      [mutation]
    ),
    {
      tokens: {
        token,
        webplayerToken
      },
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
